import { z } from "zod";

import {
  addressSchema,
  dateString,
  endDateAfterStart,
  optionalText,
  requiredDate,
  requiredWhen
} from "@/lib/forms/schema";

import type {
  CreateForeignNationalAddressHistoryEntryRequest,
  ForeignNationalAddressHistoryEntry
} from "../../../../api/types/foreignNationalAddressHistory";
import {
  addressFromRaw,
  addressToPayload,
  addressesEqual,
  emptyAddress,
  type AddressValues
} from "../../lib/address";
import { normalizeText, toDateInputValue } from "../../lib/format";
import { byCurrentThenStartDesc } from "../../lib/records";

export type AddressDraft = {
  address: AddressValues;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  notes: string;
};

export type AddressPayload = CreateForeignNationalAddressHistoryEntryRequest;

export const emptyAddressDraft = (): AddressDraft => ({
  address: emptyAddress(),
  startDate: "",
  endDate: "",
  isCurrent: false,
  notes: ""
});

export const addressEntryToDraft = (
  e: ForeignNationalAddressHistoryEntry
): AddressDraft => ({
  address: addressFromRaw(e.address),
  startDate: toDateInputValue(e.start_date),
  endDate: toDateInputValue(e.end_date),
  isCurrent: Boolean(e.is_current),
  notes: normalizeText(e.notes)
});

export const addressDraftsEqual = (a: AddressDraft, b: AddressDraft): boolean =>
  normalizeText(a.startDate) === normalizeText(b.startDate) &&
  normalizeText(a.endDate) === normalizeText(b.endDate) &&
  a.isCurrent === b.isCurrent &&
  normalizeText(a.notes) === normalizeText(b.notes) &&
  addressesEqual(a.address, b.address);

/** Full address, a from date, and a to date unless the entry is current. */
export const addressDraftSchema: z.ZodType<AddressDraft, AddressDraft> =
  endDateAfterStart(
    requiredWhen(
      z.object({
        address: addressSchema({ required: true }),
        startDate: requiredDate("From date"),
        endDate: dateString(),
        isCurrent: z.boolean(),
        notes: optionalText()
      }),
      "endDate",
      (d) => !d.isCurrent,
      "To date is required unless this is the current address."
    ),
    "startDate",
    "endDate",
    "To date can't be before the from date."
  );

/** `is_current` clears the end date. */
export const addressDraftToPayload = (d: AddressDraft): AddressPayload => ({
  address: addressToPayload(d.address),
  start_date: normalizeText(d.startDate),
  end_date: d.isCurrent ? null : normalizeText(d.endDate) || null,
  is_current: d.isCurrent,
  notes: normalizeText(d.notes) || null
});

export const sortAddressEntries =
  byCurrentThenStartDesc<ForeignNationalAddressHistoryEntry>(
    (e) => e.start_date,
    (e) => Boolean(e.is_current)
  );

export const addressFocusDomId = (id: string): string =>
  `address-history-${id}`;
